import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  approveOrganizationBillingApi,
  getOrganizationApi,
  getOrganizationRosterApi,
  recalculateOrganizationPricingApi,
} from '../api/organizations.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { formatMoney } from '../features/sponsor-pricing/sponsorPricingFormatters';

const SETUP_STATUS_LABELS = {
  draft: 'Draft',
  pending_billing: 'Pending billing',
  pending_review: 'Pending review',
  active: 'Active',
};

const STAT_ICONS = {
  Users: { icon: 'fa-users', bg: '#EFF6FF', color: '#1D4ED8' },
  Paid: { icon: 'fa-credit-card', bg: '#F0FDF4', color: '#15803D' },
  Freemium: { icon: 'fa-user', bg: '#F1F5F9', color: '#64748B' },
  'TC active': { icon: 'fa-shield-halved', bg: '#FFF7ED', color: '#C2410C' },
};

const TYPE_CLS = {
  Sponsor: 'org-type-employer',
  Referral: 'org-type-partner',
  Employer: 'org-type-employer',
  University: 'org-type-university',
  Church: 'org-type-church',
  Partner: 'org-type-partner',
};

function formatSetupStatus(value) {
  return SETUP_STATUS_LABELS[value] || value || '—';
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function initials(name) {
  return (name || 'OR')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

function toastStyle(type) {
  if (type === 'success') {
    return { background: '#F0FDF4', border: '1px solid #b7e5bb', color: '#15803D' };
  }
  if (type === 'error') {
    return { background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' };
  }
  return { background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1D4ED8' };
}

function OrgStatusBadge({ status }) {
  const key = (status || '').toLowerCase();
  const cls = {
    active: 'od-status-active',
    pending: 'od-status-pending',
    inactive: 'od-status-inactive',
    suspended: 'od-status-inactive',
  }[key] || 'od-status-inactive';
  return (
    <span className={`od-status-badge ${cls}`}>
      <span className="od-status-dot" />
      {status || '—'}
    </span>
  );
}

function SetupStatusBadge({ status }) {
  const key = status || 'draft';
  return (
    <span className={`od-setup-badge od-setup-${key}`}>
      {formatSetupStatus(key)}
    </span>
  );
}

function InfoList({ rows }) {
  return (
    <div className="od-info-list">
      {rows.map(({ key, value, icon }) => (
        <div key={key} className="od-info-row">
          <span className="od-info-key">
            {icon && <i className={`fas ${icon}`} />}
            {key}
          </span>
          <span className="od-info-val">{value ?? '—'}</span>
        </div>
      ))}
    </div>
  );
}

export default function OrganizationDetail() {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState(
    ['sponsor', 'roster', 'attribution', 'members'].includes(tabParam) ? tabParam : 'overview'
  );
  const [roster, setRoster] = useState([]);
  const [approving, setApproving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const {
    page,
    limit,
    loading: rosterLoading,
    setLoading: setRosterLoading,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(20);

  function showToast(msg, type = 'info') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }

  function handleTabChange(tab) {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === 'overview') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    setSearchParams(next, { replace: true });
  }

  async function loadOrg() {
    setLoading(true);
    const result = await apiHandler(() => getOrganizationApi(orgId));
    if (result.success) {
      setData(unwrapData(result));
    } else {
      showToast(result.message || 'Failed to load organization', 'error');
    }
    setLoading(false);
  }

  async function loadRoster(pageNum = page) {
    setRosterLoading(true);
    const result = await apiHandler(() => getOrganizationRosterApi(orgId, { page: pageNum, limit }));
    if (result.success) {
      const payload = unwrapData(result);
      setRoster(payload?.roster || []);
      applyPaginationMeta(payload);
    } else {
      showToast(result.message || 'Failed to load roster', 'error');
    }
    setRosterLoading(false);
  }

  useEffect(() => {
    if (orgId) loadOrg();
  }, [orgId]);

  useEffect(() => {
    if (!data?.enterpriseSetup && ['sponsor', 'roster', 'attribution'].includes(activeTab)) {
      setActiveTab('overview');
    }
  }, [data, activeTab]);

  useEffect(() => {
    if (orgId && activeTab === 'roster') {
      loadRoster(page);
    }
  }, [orgId, activeTab, page]);

  const org = data?.organization || {};
  const metrics = data?.metrics || {};
  const campaigns = data?.campaigns || [];
  const enterprise = data?.enterpriseSetup;
  const members = data?.raw?.userOrganizations || [];
  const fromUserId = searchParams.get('fromUser');
  const fromUserMember = members.find((m) => m.userId === fromUserId)?.user;
  const fromUserName = fromUserMember ? `${fromUserMember.firstName || ''} ${fromUserMember.lastName || ''}`.trim() : null;
  const orgName = org.name || org.organization || 'Organization';
  const setupTabLabel = (enterprise?.orgType || org.type || '').toLowerCase() === 'referral'
    ? 'Referral Setup'
    : 'Sponsor Setup';
  const isReferralOrg = (enterprise?.orgType || org.type || '').toLowerCase() === 'referral';

  const stats = [
    { label: 'Users', num: metrics.users },
    { label: 'Paid', num: metrics.paid },
    { label: 'Freemium', num: metrics.freemium },
    { label: 'TC active', num: metrics.tcActive },
  ];

  const handleApproveBilling = async () => {
    if (!window.confirm('Approve billing and activate this sponsor organization?')) return;
    setApproving(true);
    const result = await apiHandler(() => approveOrganizationBillingApi(orgId));
    if (result.success) {
      showToast('Billing approved and organization activated', 'success');
      loadOrg();
    } else {
      showToast(result.message || 'Failed to approve billing', 'error');
    }
    setApproving(false);
  };

  const handleRecalculatePricing = async () => {
    setRecalculating(true);
    const result = await apiHandler(() => recalculateOrganizationPricingApi(orgId));
    if (result.success) {
      showToast('Pricing recalculated', 'success');
      loadOrg();
    } else {
      showToast(result.message || 'Failed to recalculate pricing', 'error');
    }
    setRecalculating(false);
  };

  const copyInviteUrl = async () => {
    if (!enterprise?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(enterprise.inviteUrl);
      showToast('Invite URL copied', 'success');
    } catch {
      showToast('Could not copy invite URL', 'error');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'fa-chart-pie' },
    { id: 'members', label: 'Members', icon: 'fa-users' },
    ...(enterprise ? [
      { id: 'sponsor', label: setupTabLabel, icon: 'fa-building-columns' },
      ...(isReferralOrg ? [{ id: 'attribution', label: 'Attribution', icon: 'fa-hand-holding-dollar' }] : []),
      { id: 'roster', label: 'Roster', icon: 'fa-list-check' },
    ] : []),
  ];

  return (
    <>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 70,
            right: 20,
            zIndex: 9999,
            padding: '10px 18px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,.08)',
            ...toastStyle(toast.type),
          }}
        >
          {toast.msg}
        </div>
      )}

      <PageHeader
        title="Organization Detail"
        icon="fa-building"
        trail={[
          { label: 'Organizations', to: '/organizations' },
          { label: orgName },
        ]}
        actions={(
          <div style={{ display: 'flex', gap: 10 }}>
            {fromUserId && (
              <button
                type="button"
                className="od-header-btn od-header-btn-ghost"
                onClick={() => navigate(`/users/${fromUserId}`)}
                style={{ border: '1px solid #CBD5E1', background: '#F8FAFC', color: '#334155' }}
              >
                <i className="fas fa-user" style={{ marginRight: 4 }} />
                Back to {fromUserName || 'User'}
              </button>
            )}
            <button
              type="button"
              className="od-header-btn od-header-btn-ghost"
              onClick={() => navigate('/organizations')}
            >
              <i className="fas fa-arrow-left" />
              Back to Organizations
            </button>
          </div>
        )}
      />

      {loading ? (
        <div className="od-empty-state">
          <i className="fas fa-spinner fa-spin" />
          Loading organization…
        </div>
      ) : !data ? (
        <div className="od-empty-state">
          <i className="fas fa-building-circle-xmark" />
          Organization not found.
        </div>
      ) : (
        <div className="fade-up">
          <div className="al-stat-row" style={{ marginBottom: 20 }}>
            {stats.map((s) => {
              const meta = STAT_ICONS[s.label] || STAT_ICONS.Users;
              return (
                <div key={s.label} className="al-stat-card">
                  <div className="al-stat-icon" style={{ background: meta.bg, color: meta.color }}>
                    <i className={`fas ${meta.icon}`} />
                  </div>
                  <div>
                    <div className="al-stat-num">{s.num ?? '—'}</div>
                    <div className="al-stat-label">{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {enterprise?.canApproveBilling && (
            <div className="od-review-banner">
              <div className="od-review-banner-text">
                <strong>Billing pending review</strong>
                Payment received — approve to activate this organization and enable associate invites.
              </div>
              <button
                type="button"
                className="action-btn btn-green"
                onClick={handleApproveBilling}
                disabled={approving}
              >
                <i className="fas fa-check" />
                {approving ? 'Approving…' : 'Approve billing'}
              </button>
            </div>
          )}

          <div className="od-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`od-tab ${activeTab === tab.id ? 'od-tab-active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <i className={`fas ${tab.icon}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="od-grid">
              <div className="od-card">
                <div className="od-org-hero">
                  <div className="od-org-avatar">{initials(orgName)}</div>
                  <div>
                    <div className="od-org-name">{orgName}</div>
                    <div className="od-org-sub">
                      {org.type || 'Organization'}
                      {org.state ? ` · ${org.state}` : ''}
                    </div>
                  </div>
                </div>
                <div className="od-card-body">
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <span className={`org-type ${TYPE_CLS[org.type] || 'org-type-default'}`}>
                      {org.type || '—'}
                    </span>
                    <OrgStatusBadge status={org.status} />
                    {enterprise && (
                      <SetupStatusBadge status={enterprise.setupStatus} />
                    )}
                  </div>
                  <InfoList
                    rows={[
                      { key: 'Contact name', value: org.contactName, icon: 'fa-user' },
                      { key: 'Contact email', value: org.contactEmail, icon: 'fa-envelope' },
                      { key: 'Contact phone', value: org.contactPhone, icon: 'fa-phone' },
                      { key: 'Notes', value: org.notes || 'No notes available.', icon: 'fa-note-sticky' },
                    ]}
                  />
                </div>
              </div>

              {enterprise ? (
                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-orange">
                        <i className="fas fa-tags" />
                      </span>
                      Enterprise summary
                    </div>
                    <SetupStatusBadge status={enterprise.setupStatus} />
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Expected associates', value: enterprise.expectedAssociates, icon: 'fa-users' },
                        { key: 'Pricing tier', value: enterprise.matchedTierNumber != null ? `Tier #${enterprise.matchedTierNumber}` : '—', icon: 'fa-layer-group' },
                        { key: 'Annual total', value: formatMoney(enterprise.expectedAnnualTotal), icon: 'fa-dollar-sign' },
                        { key: 'Billing method', value: enterprise.billingMethod?.toUpperCase() || '—', icon: 'fa-credit-card' },
                        { key: 'Paid on', value: formatDate(enterprise.enterprisePaidAt), icon: 'fa-calendar-check' },
                        { key: 'Valid until', value: formatDate(enterprise.validUntil), icon: 'fa-calendar' },
                        { key: 'Approved on', value: formatDate(enterprise.enterpriseBillingApprovedAt), icon: 'fa-calendar' },
                        { key: 'CSV uploaded', value: enterprise.csvUploaded ? 'Yes' : 'No', icon: 'fa-file-csv' },
                        { key: 'Group code', value: enterprise.referralCode, icon: 'fa-qrcode' },
                      ]}
                    />
                    <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="action-btn btn-blue"
                        onClick={() => handleTabChange('sponsor')}
                      >
                        View setup
                      </button>
                      <button
                        type="button"
                        className="od-header-btn od-header-btn-ghost"
                        onClick={() => handleTabChange('roster')}
                      >
                        View roster
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-navy">
                        <i className="fas fa-circle-info" />
                      </span>
                      Organization type
                    </div>
                  </div>
                  <div className="od-card-body">
                    <p style={{ fontSize: 13, color: '#64748B', margin: 0, lineHeight: 1.6 }}>
                      This is a legacy organization. Enterprise sponsor setup tabs are available
                      only for <strong>Sponsor</strong> and <strong>Referral</strong> org types.
                    </p>
                  </div>
                </div>
              )}

              <div className="od-card">
                <div className="od-card-header">
                  <div className="od-card-title">
                    <span className="od-card-icon od-icon-blue">
                      <i className="fas fa-bullhorn" />
                    </span>
                    Recent campaigns
                  </div>
                </div>
                <div className="od-card-body" style={{ padding: campaigns.length ? '0 0 12px' : undefined }}>
                  {campaigns.length === 0 ? (
                    <div className="od-empty-state" style={{ padding: '24px 16px' }}>
                      <i className="fas fa-inbox" />
                      No recent campaigns for this organization.
                    </div>
                  ) : (
                    <table className="od-campaign-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Open rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map((c) => (
                          <tr key={c.id}>
                            <td className="od-campaign-name">{c.campaign}</td>
                            <td>{c.status}</td>
                            <td className={c.open && c.open !== '—' ? 'od-campaign-open' : 'od-campaign-dash'}>
                              {c.open || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'sponsor' && enterprise && (
            <>
              <div className="od-card" style={{ marginBottom: 20 }}>
                <div className="od-card-header">
                  <div className="od-card-title">
                    <span className="od-card-icon od-icon-orange">
                      <i className="fas fa-clipboard-check" />
                    </span>
                    Setup &amp; billing actions
                  </div>
                  <SetupStatusBadge status={enterprise.setupStatus} />
                </div>
                <div className="od-card-body">
                  <div className="od-action-group">
                    <div className="od-action-row-2">
                      {!isReferralOrg && enterprise.canApproveBilling && (
                        <button type="button" className="action-btn btn-green od-action-full" onClick={handleApproveBilling} disabled={approving}>
                          <i className="fas fa-check" />
                          {approving ? 'Approving…' : 'Approve billing'}
                        </button>
                      )}
                      {!isReferralOrg && (
                      <button type="button" className="action-btn btn-blue od-action-full" onClick={handleRecalculatePricing} disabled={recalculating}>
                        <i className="fas fa-rotate" />
                        {recalculating ? 'Recalculating…' : 'Recalculate pricing'}
                      </button>
                      )}
                    </div>
                    <Link to="/sponsor-pricing" className="action-btn od-action-full" style={{ justifyContent: 'center', background: '#F8FAFC', border: '1px solid #E2E8F0', color: '#475569' }}>
                      <i className="fas fa-table" />
                      View pricing table
                    </Link>
                  </div>
                </div>
              </div>

              <div className="od-grid">
                {isReferralOrg ? (
                  <div className="od-card">
                    <div className="od-card-header">
                      <div className="od-card-title">
                        <span className="od-card-icon od-icon-orange"><i className="fas fa-percent" /></span>
                        Referral member offer
                      </div>
                    </div>
                    <div className="od-card-body">
                      <InfoList
                        rows={[
                          { key: 'Offer type', value: enterprise.referralPreview?.offerType || 'percentage_discount' },
                          { key: 'Discount', value: enterprise.referralPreview?.discountOfferedToMembers != null ? `${enterprise.referralPreview.discountOfferedToMembers}%` : '—' },
                          { key: 'Member annual price', value: enterprise.referralPreview?.priceDisplay || '—' },
                          { key: 'Price without discount', value: enterprise.referralPreview?.basePriceDisplay || '—' },
                          { key: 'Revenue share', value: enterprise.referralPreview?.groupShareLabel || '—' },
                          { key: 'Payout frequency', value: enterprise.referralPreview?.payoutFrequencyLabel || '—' },
                          { key: 'Payout destination', value: enterprise.referralPreview?.payoutDestination || '—' },
                          { key: 'Payment responsibility', value: enterprise.paymentResponsibility === 'user' ? 'Member pays' : enterprise.paymentResponsibility },
                        ]}
                      />
                    </div>
                  </div>
                ) : (
                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-orange"><i className="fas fa-building" /></span>
                      Sponsor group
                    </div>
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Advisor', value: enterprise.sponsorGroupAdvisor },
                        { key: 'Tier', value: enterprise.matchedTierNumber != null ? `Tier #${enterprise.matchedTierNumber}` : '—' },
                        { key: 'Price / associate', value: formatMoney(enterprise.pricePerAssociateAnnual) },
                        { key: 'Expected associates', value: enterprise.expectedAssociates },
                        { key: 'Annual total', value: formatMoney(enterprise.expectedAnnualTotal) },
                        { key: 'Billing responsibility', value: enterprise.billingResponsibility },
                        { key: 'Onboarding', value: enterprise.onboardingManagement },
                      ]}
                    />
                  </div>
                </div>
                )}

                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-navy"><i className="fas fa-file-invoice-dollar" /></span>
                      Billing contact
                    </div>
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Name', value: enterprise.billingContact?.name },
                        { key: 'Title', value: enterprise.billingContact?.title },
                        { key: 'Email', value: enterprise.billingContact?.email },
                        { key: 'Phone', value: enterprise.billingContact?.phone },
                        { key: 'Address', value: enterprise.billingContact?.address },
                      ]}
                    />
                  </div>
                </div>

                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-blue"><i className="fas fa-user-plus" /></span>
                      Onboarding &amp; invite
                    </div>
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Contact', value: enterprise.onboardingContact?.name },
                        { key: 'Email', value: enterprise.onboardingContact?.email },
                        { key: 'Phone', value: enterprise.onboardingContact?.phone },
                        { key: 'Group code', value: enterprise.referralCode },
                        { key: 'Invite URL', value: enterprise.inviteUrl ? 'Configured' : '—' },
                      ]}
                    />
                    <div className="od-qr-wrap">
                      {enterprise.qrCodeDataUrl && (
                        <img src={enterprise.qrCodeDataUrl} alt="Group invite QR code" className="od-qr-img" />
                      )}
                      <div style={{ flex: 1 }}>
                        {enterprise.inviteUrl && (
                          <button type="button" className="action-btn btn-blue" style={{ width: '100%', justifyContent: 'center' }} onClick={copyInviteUrl}>
                            <i className="fas fa-copy" />
                            Copy invite URL
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="od-card">
                  <div className="od-card-header">
                    <div className="od-card-title">
                      <span className="od-card-icon od-icon-orange"><i className="fas fa-list" /></span>
                      Roster summary
                    </div>
                  </div>
                  <div className="od-card-body">
                    <InfoList
                      rows={[
                        { key: 'Total rows', value: enterprise.rosterSummary?.total },
                        { key: 'Active / pending', value: enterprise.rosterSummary?.active },
                        { key: 'Forfeited', value: enterprise.rosterSummary?.forfeited },
                      ]}
                    />
                    <button
                      type="button"
                      className="od-header-btn od-header-btn-ghost"
                      style={{ marginTop: 14, width: '100%', justifyContent: 'center' }}
                      onClick={() => handleTabChange('roster')}
                    >
                      Open full roster
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'attribution' && enterprise && isReferralOrg && (
            <div className="od-card">
              <div className="od-card-header">
                <div className="od-card-title">
                  <span className="od-card-icon od-icon-orange"><i className="fas fa-hand-holding-dollar" /></span>
                  Referral attribution
                </div>
              </div>
              <div className="od-card-body">
                <InfoList
                  rows={[
                    { key: 'Revenue share', value: enterprise.referralPreview?.groupShareLabel || '—' },
                    { key: 'Payout frequency', value: enterprise.referralPreview?.payoutFrequencyLabel || '—' },
                    { key: 'Payout destination', value: enterprise.referralPreview?.payoutDestination || '—' },
                    { key: 'Member discount', value: enterprise.referralPreview?.discountOfferedToMembers != null ? `${enterprise.referralPreview.discountOfferedToMembers}%` : '—' },
                  ]}
                />
                <p style={{ fontSize: 13, color: '#64748B', marginTop: 12 }}>
                  Detailed attribution ledger is recorded when referral members complete payment.
                  Use the owner API <code>GET /enterprise/setup/:orgId/referral-stats</code> for live counts.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="org-table-card">
              <div className="org-table-header">
                <div>
                  <div className="org-table-title">
                    <i className="fas fa-users" />
                    Joined Members
                  </div>
                  <div className="org-table-meta">
                    {members.length} member{members.length !== 1 ? 's' : ''} in this organization
                  </div>
                </div>
              </div>
              <div className="org-table-wrap">
                <table className="org-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '24px 0', color: '#64748B' }}>
                          No joined members found.
                        </td>
                      </tr>
                    ) : (
                      members.map((memberOrg) => {
                        const u = memberOrg.user || {};
                        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—';
                        return (
                          <tr key={memberOrg.id}>
                            <td>
                              <Link to={`/users/${u.id}`} className="org-name-text" style={{ textDecoration: 'none', color: '#1E293B', fontWeight: 600 }}>
                                {name}
                              </Link>
                              {memberOrg.role === 'owner' && (
                                <span style={{ marginLeft: 8, fontSize: 10, background: '#EFF6FF', color: '#1D4ED8', padding: '2px 6px', borderRadius: 4, fontWeight: 500 }}>
                                  Owner
                                </span>
                              )}
                            </td>
                            <td>{u.email || '—'}</td>
                            <td>{u.mobile || '—'}</td>
                            <td>
                              <span style={{ fontSize: 12, textTransform: 'capitalize' }}>
                                {memberOrg.role || 'member'}
                              </span>
                            </td>
                            <td>
                              <span className={`org-status org-status-${u.status === 'active' ? 'active' : 'pending'}`}>
                                {u.status || 'pending'}
                              </span>
                            </td>
                            <td>{formatDate(memberOrg.joinedAt)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'roster' && enterprise && (
            <div className="org-table-card">
              <div className="org-table-header">
                <div>
                  <div className="org-table-title">
                    <i className="fas fa-list-check" />
                    Associate roster
                  </div>
                  <div className="org-table-meta">
                    {roster.length} row{roster.length !== 1 ? 's' : ''} on this page
                  </div>
                </div>
              </div>
              <div className="org-table-wrap">
                <table className="org-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Status</th>
                      <th>Activation deadline</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rosterLoading ? (
                      <tr><td colSpan={5}>Loading roster…</td></tr>
                    ) : roster.length === 0 ? (
                      <tr><td colSpan={5}>No roster rows found.</td></tr>
                    ) : roster.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span className="org-name-text">
                            {`${row.firstName || ''} ${row.lastName || ''}`.trim() || '—'}
                          </span>
                        </td>
                        <td>{row.email || '—'}</td>
                        <td>{row.mobile || '—'}</td>
                        <td>
                          <span className={`org-status org-status-${row.status === 'active' ? 'active' : 'pending'}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>{row.activationDeadline ? new Date(row.activationDeadline).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="org-footer">
                <Pagination {...paginationProps} />
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
